import React, { Dispatch, memo } from 'react';

import isEqual from 'lodash.isequal';
import { injectIntl } from 'react-intl';
import { Context, useContextSelector } from 'use-context-selector';

import translateHookTs from '../Lang/TranslateHookTS';

interface IMapStateToProps<S, P> {
    (state: S | P, isComparison?: boolean): P
}

interface IBaseStore {
    store: any,
    dispatch: Dispatch<any>
}

interface IReducerAction {
    data?: any,
    type: string
}

interface IReducerActionWithField extends IReducerAction {
    field?: string
}

const returnPropsAsDefault = (_store: any, props: any) => props;

function useMemoStrict<P>(mapStateToProps = returnPropsAsDefault) {
    return (Component: any) => {
        const propsAreEqual = (_prev: P, _next: P) => {
            const prev = mapStateToProps(_prev, true);
            const next = mapStateToProps(_next, true);
            return isEqual(prev, next);
        };

        return memo(Component, propsAreEqual);
    };
}

function connectBase<C, S, P, A>(context: Context<C>, mapStateToProps: IMapStateToProps<S, P> = returnPropsAsDefault) {
    return (WrappedComponent: React.ComponentType<P>) => {
        const displayName = WrappedComponent.displayName ||
            (WrappedComponent as any).type.displayName ||
            WrappedComponent.name ||
            'Component';

        const ComponentWithContext = (props: Omit<A, keyof P>) => {
            const store = useContextSelector<C, P>(context, (data) => ((data as unknown) as IBaseStore).store);
            const dispatch = useContextSelector<C, Dispatch<any>>(context, (data) => ((data as unknown) as IBaseStore).dispatch);

            const storeProps = mapStateToProps(store);

            return <WrappedComponent {...props} {...storeProps} dispatch={dispatch} />;
        };

        ComponentWithContext.displayName = `connect(${displayName})`;

        return ComponentWithContext;
    };
}

function connect<C, S, P, A = null>(context: Context<C>, mapStateToProps: IMapStateToProps<S, P> = returnPropsAsDefault) {
    return (Component: React.ComponentType<any>) => connectBase<C, S, P, A>(context, mapStateToProps)(useMemoStrict(mapStateToProps)(injectIntl(Component)));
}

function connectIntl<C, S, P, A = null>(context: Context<C>, mapStateToProps: IMapStateToProps<S, P> = returnPropsAsDefault) {
    return (Component: React.ComponentType<any>) => connectBase<C, S, P, A>(context, mapStateToProps)(useMemoStrict(mapStateToProps)(translateHookTs(injectIntl(Component))));
}

export {
    connectIntl,

    IReducerAction,
    IMapStateToProps,
    IReducerActionWithField
};

export default connect;
